import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { drag } from 'd3-drag';
import { select } from 'd3-selection';
import { translate, nf } from '~/utils';
import useStore from '~/store';
import useColumnSum from '~/hooks/useColumnSum';
import { margin } from '~/../config.json';
import { media } from '~/styles/Utils';

import { ChartLabel } from './ChartComponents';

const Handle = styled.rect`
  stroke-width: 2px;
  stroke: ${(p) => p.theme.colors.blue};
  fill: white;
  cursor: move;

  ${media.m`
    &:hover {
      fill: ${(p) => p.theme.colors.blue};
    }
  `}
`;

const Label = styled(ChartLabel)`
  user-select: none;
  pointer-events: none;
  font-size: 14px;
  text-anchor: middle;
  font-weight: bold;
`;

const Triangle = styled.path`
  fill: ${(p) => p.theme.colors.blue};
`;

const TriangleUp = styled(Triangle).attrs({
  d: 'M 5,0 L 0,6 L 10,6 z',
})``;

const TriangleDown = styled(Triangle).attrs({
  d: 'M 0,0 L 10,0 L 5,6 z',
})``;

const BarHighlightRect = styled.rect`
  fill: none;
  stroke: #333;
  stroke-width: 2;
  shape-rendering: crispEdges;
  user-select: none;
  pointer-events: none;
`;

const SumLabel = styled(ChartLabel)`
  font-weight: bold;
  text-shadow: -1px -1px 0 #fff, 1px -1px 0 #fff, -1px 1px 0 #fff, 1px 1px 0 #fff;
`;

const ageSelector = (s) => s.age;
const setAgeSelector = (s) => s.setAge;

function DatumHighlight({ xScale, yScale, height, width, outerWidth }) {
  const clampedXScale = xScale.copy().clamp(true);
  const ref = useRef(null);
  const age = useStore(ageSelector);
  const setAge = useStore(setAgeSelector);
  const { leftSum, rightSum } = useColumnSum();
  const leftWidth = xScale(leftSum);
  const rightWidth = xScale(rightSum);
  const leftX = width / 2 - leftWidth;
  const rightX = width / 2 + margin.center;

  const y = yScale(age);

  useEffect(() => {
    const onDrag = (evt) => {
      const dragAge = yScale.invert(evt.y);
      setAge(Math.round(dragAge));
    };

    select(ref.current).call(drag().on('drag', onDrag));
  }, []);

  const leftXClamped = width / 2 - clampedXScale(leftSum);
  const labelLeftX = Math.max(0, leftXClamped - 5);
  const labelRightX = rightX + clampedXScale(rightSum) + 5;

  return (
    <g ref={ref} transform={translate(0, y)}>
      <BarHighlightRect x={leftX} y={0} width={leftWidth} height={height} />
      <SumLabel y={7} textAnchor={labelLeftX < 50 ? 'middle' : 'end'} x={labelLeftX}>
        {nf(leftSum)}
      </SumLabel>
      <g transform={translate(width / 2 + margin.center / 2, 0)}>
        <g transform={translate(-5, -18)}>
          <TriangleUp />
        </g>
        <Handle rx={4} x={-margin.center / 2 + 5} y={-8} width={margin.center - 10} height={26} />
        <Label fontWeight="bold" y={10} textAnchor="middle">
          {age}
        </Label>
        <g transform={translate(-5, 22)}>
          <TriangleDown />
        </g>
      </g>
      <BarHighlightRect x={rightX} y={0} width={rightWidth} height={height} />
      <SumLabel y={7} textAnchor={labelRightX > outerWidth - 50 ? 'end' : 'start'} x={labelRightX}>
        {nf(rightSum)}
      </SumLabel>
    </g>
  );
}

export default DatumHighlight;
